import React, { useState } from "react";
import { createUserWithEmailAndPassword, sendEmailVerification } from "firebase/auth";
import { ref, push, set, get } from "firebase/database";
import { auth, db } from "./firebase";
import { Link } from "react-router-dom";
import { FaUser, FaEnvelope, FaPhone, FaUserShield, FaBook, FaLock } from "react-icons/fa";
import Navbar from "./Navbar";
import Footer from "./Footer";

const StudentRegistration = () => {
    const [step, setStep] = useState(1);
    const [formData, setFormData] = useState({
        name: "",
        email: "",
        phone: "",
        parentPhone: "",
        course: "",
    });
    const [emailStep2, setEmailStep2] = useState("");
    const [password, setPassword] = useState("");
    const [confirmPassword, setConfirmPassword] = useState("");
    const [message, setMessage] = useState("");
    const [error, setError] = useState("");
    const [courses, setCourses] = useState([]);
    const [loading, setLoading] = useState(false);

    // Fetch available courses
    React.useEffect(() => {
        const coursesRef = ref(db, "courses");
        get(coursesRef).then((snapshot) => {
            if (snapshot.exists()) {
                const coursesData = snapshot.val();
                const courseNames = Object.keys(coursesData).map(courseId => ({
                    id: courseId,
                    name: coursesData[courseId].name || courseId
                }));
                setCourses(courseNames);
            }
        }).catch(error => {
            setError("Failed to load courses. Please try again.");
        });
    }, []);

    // Step 1 handlers
    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData({ ...formData, [name]: value });
    };

    const handleStep1Submit = (e) => {
        e.preventDefault();
        setError("");
        if (!formData.name || !formData.email || !formData.phone || !formData.parentPhone || !formData.course) {
            setError("Please fill in all fields.");
            return;
        }
        setStep(2);
    };

    // Step 2 handlers
    const handleStep2Submit = async (e) => {
        e.preventDefault();
        setError("");
        setMessage("");
        // No need to check email match, as it's disabled and comes from Step 1
        if (!password || password !== confirmPassword) {
            setError("Passwords do not match or are empty.");
            return;
        }
        setLoading(true);
        try {
            // Create user in Firebase Auth
            const userCredential = await createUserWithEmailAndPassword(auth, formData.email, password);
            const user = userCredential.user;
            await sendEmailVerification(user);
            // Save registration data to database
            const registrationRef = ref(db, "registrationRequests");
            const newRegistrationRef = push(registrationRef);
            const registrationData = {
                name: formData.name,
                email: formData.email,
                phone: formData.phone,
                parentPhone: formData.parentPhone,
                course: formData.course,
                status: "pending",
                userId: user.uid,
                createdAt: new Date().toISOString()
            };
            await set(newRegistrationRef, registrationData);
            // Add to /students/{emailKey}
            function normalizeEmail(email) {
                return email.trim().toLowerCase().replace(/\./g, ',');
            }
            const emailKey = normalizeEmail(formData.email);
            await set(ref(db, `students/${emailKey}`), {
                ...registrationData,
                course: formData.course,
            });
            setMessage("Registration submitted successfully!");
            setFormData({ name: "", email: "", phone: "", parentPhone: "", course: "" });
            setPassword("");
            setConfirmPassword("");
            setStep(1);
        } catch (err) {
            if (err.code === 'auth/email-already-in-use') {
                setError("This email is already registered. Please use a different email or try logging in.");
            } else if (err.code === 'auth/weak-password') {
                setError("Password should be at least 6 characters long.");
            } else if (err.code === 'auth/invalid-email') {
                setError("Please enter a valid email address.");
            } else {
                setError("An error occurred during registration. Please try again.");
            }
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="font-sans bg-gray-900 text-white min-h-screen flex flex-col">
            <Navbar />
            <div className="flex-grow flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8">
                <div className="max-w-md w-full space-y-8">
                    <div className="bg-gradient-to-br from-gray-800 to-gray-900 border border-gray-700 rounded-2xl p-8 shadow-2xl backdrop-blur-sm">
                        <div>
                            <h2 className="mt-6 text-center text-3xl font-extrabold bg-clip-text text-transparent bg-gradient-to-r from-blue-400 to-sky-300">
                                Student Registration
                            </h2>
                            <p className="mt-2 text-center text-sm text-gray-400">
                                Create your account to get started
                            </p>
                        </div>
                        {message && (
                            <div className="mt-4 p-4 bg-green-500/10 border border-green-500/20 rounded-lg">
                                <p className="text-sm text-green-400 text-center">{message}</p>
                            </div>
                        )}
                        {error && (
                            <div className="mt-4 p-4 bg-red-500/10 border border-red-500/20 rounded-lg">
                                <p className="text-sm text-red-400 text-center">{error}</p>
                            </div>
                        )}
                        {step === 1 && !message && (
                            <form className="mt-8 space-y-6" onSubmit={handleStep1Submit}>
                                <div className="space-y-4">
                                    {/* Name */}
                                    <div className="relative">
                                        <label className="block text-sm font-medium text-gray-400 mb-2">
                                            <FaUser className="inline mr-2" />
                                            Full Name
                                        </label>
                                        <input
                                            type="text"
                                            name="name"
                                            value={formData.name}
                                            onChange={handleChange}
                                            className="w-full pl-10 pr-4 py-3 bg-gray-800 border border-gray-700 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition text-white placeholder-gray-500"
                                            placeholder="Enter your full name"
                                            required
                                        />
                                    </div>
                                    {/* Email */}
                                    <div className="relative">
                                        <label className="block text-sm font-medium text-gray-400 mb-2">
                                            <FaEnvelope className="inline mr-2" />
                                            Email
                                        </label>
                                        <input
                                            type="email"
                                            name="email"
                                            value={formData.email}
                                            onChange={handleChange}
                                            className="w-full pl-10 pr-4 py-3 bg-gray-800 border border-gray-700 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition text-white placeholder-gray-500"
                                            placeholder="Enter your email"
                                            required
                                        />
                                    </div>
                                    {/* Phone */}
                                    <div className="relative">
                                        <label className="block text-sm font-medium text-gray-400 mb-2">
                                            <FaPhone className="inline mr-2" />
                                            WhatsApp Number
                                        </label>
                                        <input
                                            type="tel"
                                            name="phone"
                                            value={formData.phone}
                                            onChange={handleChange}
                                            className="w-full pl-10 pr-4 py-3 bg-gray-800 border border-gray-700 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition text-white placeholder-gray-500"
                                            placeholder="Enter your number"
                                            required
                                        />
                                    </div>
                                    {/* Parent Phone */}
                                    <div className="relative">
                                        <label className="block text-sm font-medium text-gray-400 mb-2">
                                            <FaUserShield className="inline mr-2" />
                                            Parent's WhatsApp
                                        </label>
                                        <input
                                            type="tel"
                                            name="parentPhone"
                                            value={formData.parentPhone}
                                            onChange={handleChange}
                                            className="w-full pl-10 pr-4 py-3 bg-gray-800 border border-gray-700 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition text-white placeholder-gray-500"
                                            placeholder="Enter parent's number"
                                            required
                                        />
                                    </div>
                                    {/* Course */}
                                    <div className="relative">
                                        <label className="block text-sm font-medium text-gray-400 mb-2">
                                            <FaBook className="inline mr-2" />
                                            Course
                                        </label>
                                        {courses.length === 0 ? (
                                            <div className="text-red-500 text-sm">
                                                No courses available. Please contact the administrator.
                                            </div>
                                        ) : (
                                            <select
                                                name="course"
                                                value={formData.course}
                                                onChange={handleChange}
                                                className="w-full pl-10 pr-4 py-3 bg-gray-800 border border-gray-700 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition text-white"
                                                required
                                            >
                                                <option value="" disabled className="text-white bg-gray-800">Select Course</option>
                                                {courses.map(course => (
                                                    <option key={course.id} value={course.id} className="text-white bg-gray-800">
                                                        {course.name}
                                                    </option>
                                                ))}
                                            </select>
                                        )}
                                    </div>
                                </div>
                                <div>
                                    <button
                                        type="submit"
                                        className="w-full px-8 py-3 rounded-full bg-gradient-to-r from-blue-600 to-sky-600 hover:from-blue-500 hover:to-sky-500 text-white font-medium transition-all duration-300 shadow-lg hover:shadow-blue-500/30"
                                        disabled={loading}
                                    >
                                        {loading ? "Loading..." : "Continue"}
                                    </button>
                                </div>
                            </form>
                        )}
                        {step === 2 && !message && (
                            <form className="mt-8 space-y-6" onSubmit={handleStep2Submit}>
                                <div className="space-y-4">
                                    {/* Email (disabled) */}
                                    <div className="relative">
                                        <label className="block text-sm font-medium text-gray-400 mb-2">
                                            <FaEnvelope className="inline mr-2" />
                                            Email
                                        </label>
                                        <input
                                            type="email"
                                            name="emailStep2"
                                            value={formData.email}
                                            disabled
                                            className="w-full pl-10 pr-4 py-3 bg-gray-700 border border-gray-700 rounded-lg text-gray-400 cursor-not-allowed"
                                        />
                                    </div>
                                    {/* Password */}
                                    <div className="relative">
                                        <label className="block text-sm font-medium text-gray-400 mb-2">
                                            <FaLock className="inline mr-2" />
                                            Password
                                        </label>
                                        <input
                                            type="password"
                                            name="password"
                                            value={password}
                                            onChange={e => setPassword(e.target.value)}
                                            className="w-full pl-10 pr-4 py-3 bg-gray-800 border border-gray-700 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition text-white placeholder-gray-500"
                                            placeholder="Enter password"
                                            required
                                            minLength={6}
                                        />
                                    </div>
                                    {/* Confirm Password */}
                                    <div className="relative">
                                        <label className="block text-sm font-medium text-gray-400 mb-2">
                                            <FaLock className="inline mr-2" />
                                            Confirm Password
                                        </label>
                                        <input
                                            type="password"
                                            name="confirmPassword"
                                            value={confirmPassword}
                                            onChange={e => setConfirmPassword(e.target.value)}
                                            className="w-full pl-10 pr-4 py-3 bg-gray-800 border border-gray-700 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition text-white placeholder-gray-500"
                                            placeholder="Re-enter password"
                                            required
                                            minLength={6}
                                        />
                                    </div>
                                </div>
                                <div className="flex gap-4">
                                    <button
                                        type="button"
                                        className="w-1/2 px-8 py-3 rounded-full bg-gray-700 hover:bg-gray-600 text-white font-medium transition-all duration-300 shadow-lg"
                                        onClick={() => setStep(1)}
                                        disabled={loading}
                                    >
                                        Back
                                    </button>
                                    <button
                                        type="submit"
                                        className="w-1/2 px-8 py-3 rounded-full bg-gradient-to-r from-blue-600 to-sky-600 hover:from-blue-500 hover:to-sky-500 text-white font-medium transition-all duration-300 shadow-lg hover:shadow-blue-500/30"
                                        disabled={loading}
                                    >
                                        {loading ? "Registering..." : "Register"}
                                    </button>
                                </div>
                            </form>
                        )}
                        <p className="mt-6 text-center text-gray-400">
                            Already have an account?{" "}
                            <Link to="/" className="text-blue-400 hover:text-blue-300">
                                Login
                            </Link>
                        </p>
                    </div>
                </div>
            </div>
            <Footer />
        </div>
    );
};

export default StudentRegistration; 